import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  UserPlus, FileText, AlertCircle, CheckCircle,
  Clock, TrendingUp, Download, Send, Calendar
} from 'lucide-react';
import { bookingsAPI, customersAPI, labResultsAPI } from '../services/api';

const ActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      // Fetch recent data
      const [bookingsRes, customersRes, resultsRes] = await Promise.all([
        bookingsAPI.getAll({ limit: 10 }),
        customersAPI.getAll({ limit: 10 }),
        labResultsAPI.getAll({ limit: 10 }).catch(() => ({ data: { data: { items: [] } } }))
      ]);

      const bookings = bookingsRes.data?.data?.items || bookingsRes.data?.items || [];
      const customers = customersRes.data?.data?.items || customersRes.data?.items || [];
      const results = resultsRes.data?.data?.items || resultsRes.data?.items || [];

      const activityList = [];

      // Add recent customers
      customers.slice(0, 2).forEach(customer => {
        const createdDate = new Date(customer.created_at);
        const timeAgo = getTimeAgo(createdDate);
        activityList.push({
          id: `customer-${customer._id}`,
      icon: UserPlus,
      color: 'blue',
      title: 'مريض جديد',
          description: `تم تسجيل مريض جديد: ${customer.full_name || 'غير معروف'}`,
          time: timeAgo,
          date: createdDate
        });
      });

      // Add recent bookings
      bookings.slice(0, 2).forEach(booking => {
        const createdDate = new Date(booking.created_at || booking.booking_date);
        const timeAgo = getTimeAgo(createdDate);
        const customerName = booking.customer_id?.full_name || 'غير معروف';
        activityList.push({
          id: `booking-${booking._id}`,
          icon: Calendar,
          color: 'purple',
          title: 'حجز جديد',
          description: `تم إنشاء حجز جديد للعميل: ${customerName}`,
          time: timeAgo,
          date: createdDate
        });
      });

      // Add completed results (filter for completed status)
      results
        .filter(result => result.status === 'completed' || result.status === 'approved' || result.status === 'released')
        .slice(0, 2)
        .forEach(result => {
          const createdDate = new Date(result.created_at || result.updated_at || new Date());
          const timeAgo = getTimeAgo(createdDate);
          const patientName = result.patient_name || 'غير معروف';
          const testName = result.test_name || 'فحص';
          activityList.push({
            id: `result-${result._id || result.booking_test_id || Math.random()}`,
      icon: CheckCircle,
      color: 'green',
      title: 'نتيجة جاهزة',
            description: `اكتملت نتائج فحص ${testName} للمريض ${patientName}`,
            time: timeAgo,
            date: createdDate
          });
        });

      // Sort by date (most recent first) and limit to 5
      activityList.sort((a, b) => b.date - a.date);
      setActivities(activityList.slice(0, 5));

    } catch (error) {
      console.error('Error fetching activities:', error);
      // Fallback to empty array
      setActivities([]);
    }
    setLoading(false);
  };

  const getTimeAgo = (date) => {
    if (!date) return 'منذ وقت غير محدد';
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `منذ ${days} يوم`;
    if (hours > 0) return `منذ ${hours} ساعة`;
    if (minutes > 0) return `منذ ${minutes} دقيقة`;
    return 'الآن';
  };

  const getIconColors = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600',
    };
    return colors[color] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">آخر النشاطات</h3>
        <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">عرض الكل</button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : activities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <Clock className="w-12 h-12 mb-2" />
          <p className="text-sm">لا توجد نشاطات حديثة</p>
        </div>
      ) : (
      <div className="space-y-4">
        {activities.map((activity, index) => {
          const Icon = activity.icon;
          return (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start gap-3"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getIconColors(activity.color)}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900">{activity.title}</h4>
                <p className="text-sm text-gray-600">{activity.description}</p>
                <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default ActivityFeed;
